import React, { useState } from 'react';
import { FiChevronDown, FiChevronUp, FiBarChart2, FiFilter } from 'react-icons/fi';
import StatsTable from './StatsTable';
import RankingsGrid from './RankingsGrid';
import { mapPools } from './constants';

const ModeSection = ({
  mode,
  data,
  insights = [],
  rankings,
  mapFilter,
  onMapFilterChange,
  loading,
  sortConfig,
  onSort,
  playerSearch,
  comparePlayers,
  onPlayerClick,
  onToggleComparison,
  playerImages
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showRankings, setShowRankings] = useState(false);
  const [statsView, setStatsView] = useState('avg');

  const modeLabels = {
    HP: 'Hardpoint Mode',
    SND: 'Search & Destroy Mode',
    CTL: 'Control Mode'
  };
  const insightsLabels = {
    HP: 'Hardpoint Insights',
    SND: 'Search & Destroy Insights',
    CTL: 'Control Insights',
  };

  const hasData = data && data.length > 0;
  const hasRankings = (mode === 'HP' || mode === 'SND' || mode === 'CTL') && Array.isArray(rankings) && rankings.length > 0;

  return (
    <div className="mode-section-block dashboard-surface">
      {/* Collapsible Header */}
      <div 
        className="mode-section-header" 
        onClick={() => setIsExpanded(!isExpanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }
        }}
      >
        <div className="mode-header-content">
          <h2>{modeLabels[mode]}</h2>
          {hasData && (
            <span className="mode-data-count">{data.length} {data.length === 1 ? 'player' : 'players'}</span>
          )}
        </div>
        <button 
          className="collapse-toggle"
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
        >
          {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
        </button>
      </div>

      {isExpanded && (
        <div className="mode-section-content">
          {/* Map Filter */}
          <div className="map-filter">
            <FiFilter size={16} />
            <label>Filter by Map:</label>
            <select
              className="dashboard-select"
              value={mapFilter}
              onChange={(e) => onMapFilterChange(mode, e.target.value)}
            >
              <option value="">All Maps</option>
              {mapPools[mode].map((map, idx) => (
                <option key={idx} value={map}>
                  {map}
                </option>
              ))}
            </select>
            <div style={{ display: 'inline-flex', marginLeft: '12px', border: '1px solid #2f2f45', borderRadius: '8px', overflow: 'hidden' }}>
              <button
                type="button"
                onClick={() => setStatsView('avg')}
                style={{
                  padding: '6px 12px',
                  border: 'none',
                  cursor: 'pointer',
                  background: statsView === 'avg' ? '#7e57c2' : 'transparent',
                  color: '#fff',
                }}
              >
                Show Avg
              </button>
              <button
                type="button"
                onClick={() => setStatsView('totals')}
                style={{
                  padding: '6px 12px',
                  border: 'none',
                  cursor: 'pointer',
                  background: statsView === 'totals' ? '#7e57c2' : 'transparent',
                  color: '#fff',
                }}
              >
                Show Totals
              </button>
            </div>
          </div>

          {/* Stats Table */}
          <StatsTable
            data={data}
            loading={loading}
            sortConfig={sortConfig}
            onSort={onSort}
            playerSearch={playerSearch}
            comparePlayers={comparePlayers}
            onPlayerClick={onPlayerClick}
            onToggleComparison={onToggleComparison}
            playerImages={playerImages}
            statsView={statsView}
          />

          {Array.isArray(insights) && insights.length > 0 && (
            <div className="player-insights-section">
              <div className="player-insights-header">
                <span>Player Insights</span>
                <small>Based on current filters</small>
              </div>
              <div className="player-insights-grid">
                {insights.map((insight) => (
                  <button
                    type="button"
                    key={`${mode}-${insight.title}-${insight.player}`}
                    className="player-insight-card"
                    onClick={() => onPlayerClick(insight.player)}
                  >
                    <span className="player-insight-label">{insight.title}</span>
                    <strong>{insight.player}</strong>
                    <span className="player-insight-value">{insight.value}</span>
                    <span className="player-insight-delta">{insight.delta}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Collapsible Rankings Section */}
          {hasRankings && (
            <div className="rankings-section-wrapper">
              <button 
                className="rankings-toggle"
                onClick={() => setShowRankings(!showRankings)}
              >
                <FiBarChart2 size={18} />
                <span>{insightsLabels[mode] || 'Mode Insights'}</span>
                {showRankings ? <FiChevronUp /> : <FiChevronDown />}
              </button>
              {showRankings && (
                <div className="rankings-content">
                  <RankingsGrid
                    mode={mode}
                    rankings={rankings}
                    onPlayerClick={onPlayerClick}
                    playerImages={playerImages}
                    statsView={statsView}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ModeSection;

